// Copyright (c) StrangeBee 2024

import client from '@Client';
import ListProvider from '@Context/ListProvider';
import { useLocalMessage } from '@Helpers/locale';
import { openMessage, handleNotificationError } from '@Helpers/notification';
import useConfigList from '@Hooks/useConfigList';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import { ObservableList } from '@Organisms/Observable/ObservableList/ObservableList';
import { useGenericCountQuery, useGenericQuery } from '@Queries/genericQueries';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import React, { ReactElement, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { AnalyzerOutput, ObservableOutput, ObjectTypeV1 as ObjectType, AlertOutput } from 'thehive-sdk';
import { computeEventList as observableEventList } from '@Organisms/Observable/eventList';
import { THLayout } from '@Molecules/THLayout';
import { useStreamObserver } from '@Hooks/useStream';
import { STREAM_EVENTS } from '@Services/StreamSrv';
import { ICONS } from '@Constants/iconsApp';
import { useCurrentUser } from '@Queries/userQueries';
import usePageTitle from '@Hooks/usePageTitle';
import useOneQuery from '@Hooks/useOneQuery';
import { computeGetSelector } from '@Organisms/Alert/selector';

const AlertsObservablesList = (): ReactElement => {
    const i = useLocalMessage();

    const { alertId } = useParams<{ alertId: string }>();

    const { data: analyzerData } = useGenericQuery<AnalyzerOutput[]>({
        fetchCallback: () => client.AnalyzerController.listAnalyzer(),
        key: ['get-all-analyzer'],
    });

    const { data: alertData } = useOneQuery<AlertOutput>({
        selector: computeGetSelector({ alertId }),
        controller: client.AlertController,
    });

    const analyzer = {
        data: analyzerData,
        onFinish: (items: any[]): Promise<void> =>
            client.AnalyzerController.multipleExecuteJob(items)
                .then(() => {
                    openMessage('success', i('analyzer.launch.success'));
                })
                .catch(() => {
                    openMessage('success', i('analyzer.launch.error'));
                }),
    };

    const config = useConfigList<ObservableOutput>({
        selector: {
            head: [{ name: 'getAlert', idOrName: alertId }, { name: 'observables' }],
            extraData: ['seenSummary'],
            name: `get-alert-${alertId}-observables`,
            sort: ['-startDate'],
        },
        controller: client.ObservableController,
        storageId: StorageFilterEnum.ALERT_OBSERVABLE,
        objectType: ObjectType.OBSERVABLE,
        streamRootId: alertId,
    });

    const { listRefetch, countRefetch } = config;

    const eventList = useMemo(
        () =>
            observableEventList({
                controller: client.ObservableController,
                locale: i,
                onError: handleNotificationError,
                onSuccess: (count, message) => {
                    if (count) {
                        countRefetch();
                    }
                    if (message) {
                        openMessage('success', message);
                    }
                    listRefetch();
                },
            }),
        [countRefetch, i, listRefetch],
    );

    const { data: currentUser } = useCurrentUser();

    const { permissions = [] } = currentUser || {};

    usePageTitle(i('page-title.alerts.observables', { title: alertData?.title || '' }));

    return (
        <THLayout>
            <ObservableList
                config={config}
                eventList={eventList}
                analyzer={analyzer}
                alertId={alertId}
                permissions={permissions}
            />
        </THLayout>
    );
};

const MenuCount = (): ReactElement => {
    const { alertId } = useParams<{ alertId: string }>(); //! it's not beautiful (sorry, it's just for this step)
    const local = useLocalMessage();

    const { data: count = 0, refetch: countRefetch } = useGenericCountQuery({
        controller: client.AlertController,
        queryParams: {
            selector: {
                head: [{ name: 'getAlert', idOrName: alertId }, { name: 'observables' }],
                extraData: ['seenSummary'],
                name: `get-alert-${alertId}-observables`,
            },
        },
        fetchOnStart: true,
    });

    useStreamObserver({ rootId: alertId, objectType: STREAM_EVENTS.OBSERVABLE }, (): void => {
        countRefetch();
    });

    const processedCount = count < 0 ? `${Math.abs(count)}+` : count;

    return (
        <>
            <ICONS.ObservableIcon />
            <span className="tab-counter__label">
                {local('alert.layout.nav-bar.observables')} ({processedCount})
            </span>
        </>
    );
};
export const Route: RouteType = {
    menu: {
        label: 'alert.layout.nav-bar.observables',
        render: <MenuCount />,
    },
    path: RouteBuilder.compute('alerts.observables'),
    content: <AlertsObservablesList />,
    Context: ListProvider,
    space: ['user'],
    exact: true,
    breadcrumb: { label: 'breadcrumb.alert.observables' },
};
